import clsx from 'clsx'
import { awaitingConfirm, currentEvaluation, showingBest, useGameState } from '@/hooks/useGameState'
import { sourceKey, startTileDrag, useDrag } from '@/hooks/useTileDrag'
import { describeError, letterValue } from '@/lib/scoring'

export function LetterRack() {
  const state = useGameState()
  const dragging = useDrag((d) => d.ghost?.from)
  const overRack = useDrag((d) => d.overRack)
  const { puzzle, record, rackOrder, placed, selected, cursor, message, coach, select, shuffle, moveInRack, recallAll, submit, restoreBest } =
    state
  if (!puzzle || !record) return null

  const finished = record.finished
  const evaluation = currentEvaluation(state)
  const used = new Set(placed.map((p) => p.rackIndex))
  const confirming = !finished && evaluation?.ok === true && awaitingConfirm(state)

  return (
    <div className="space-y-3">
      <div className="rack flex justify-center gap-1.5 sm:gap-2" role="listbox" aria-label="Your letters">
        {rackOrder.map((i, pos) => {
          const letter = puzzle.player_letters[i]
          // Every position, empty or not, takes a dragged tile to move it there.
          const drop = overRack === pos && dragging !== undefined && 'is-drop'
          if (used.has(i)) {
            return <div key={i} data-rack-pos={pos} className={clsx('rack-slot aspect-square w-11 sm:w-12', drop)} />
          }
          return (
            <button
              key={i}
              type="button"
              role="option"
              aria-selected={selected === i}
              aria-label={`${letter}, ${letterValue(letter)} points`}
              aria-keyshortcuts="Shift+ArrowLeft Shift+ArrowRight"
              data-rack-pos={pos}
              data-rack-index={i}
              disabled={finished}
              onClick={() => select(i)}
              onKeyDown={(e) => {
                // Shift+arrows move the focused letter along the rack (plain arrows move the board cursor).
                const step = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
                if (!e.shiftKey || !step) return
                e.preventDefault()
                e.stopPropagation()
                moveInRack(i, pos + step)
                // React may re-insert the button while reordering, which drops focus.
                requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-rack-index="${i}"]`)?.focus())
              }}
              onPointerDown={(e) => startTileDrag(e, { rackIndex: i }, letter)}
              className={clsx(
                'rack-tile relative flex aspect-square w-11 touch-none items-center justify-center select-none disabled:opacity-40 sm:w-12',
                dragging === sourceKey({ rackIndex: i }) && 'opacity-30',
                drop,
                coach?.rackIndex === i && 'coach-target',
              )}
            >
              {letter}
              <span className="absolute right-1 bottom-0.5 font-sans text-[0.6rem] font-semibold opacity-70">{letterValue(letter)}</span>
            </button>
          )
        })}
      </div>

      {/* Fixed height (two lines), like the best-so-far slot below: the board fills the
          space the controls leave, so anything here changing size would resize it mid-game. */}
      <p className="flex h-10 items-center justify-center text-center text-sm leading-5" aria-live="polite">
        {confirming && evaluation?.ok ? (
          <span className="line-clamp-2 font-medium">
            This is your only play: {evaluation.play.words.join(' + ')} for {evaluation.play.score}. Confirm it, or move a
            tile to keep looking.
          </span>
        ) : message ? (
          <span className="line-clamp-2 font-medium">{message}</span>
        ) : evaluation?.ok ? (
          <span className="accent-text font-semibold">
            {evaluation.play.words.join(' + ')} · {evaluation.play.score} pts
          </span>
        ) : evaluation ? (
          <span className={clsx('line-clamp-2', evaluation.code === 'invalid_word' ? 'text-rose-700 dark:text-rose-400' : 'muted')}>
            {describeError(evaluation)}
          </span>
        ) : !finished ? (
          <span className="muted">
            {cursor
              ? `Type your letters (${cursor.dir === 'across' ? 'across →' : 'down ↓'}). Space switches direction.`
              : selected === null
                ? 'Tap a letter, then tap a square.'
                : 'Now tap a square on the board.'}
          </span>
        ) : null}
      </p>

      {!finished &&
        (record.bestFound && !showingBest(state) ? (
          <button
            type="button"
            onClick={restoreBest}
            className="best-card flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors"
          >
            <span className="min-w-0">
              <span className="muted block text-xs font-medium tracking-wide uppercase">Best so far</span>
              <span className="block truncate font-semibold">
                {record.bestFound.words.join(' + ')} · {record.bestFound.score} pts
              </span>
            </span>
            <span className="shrink-0 font-semibold">Put back ↺</span>
          </button>
        ) : (
          // Same slot, nothing to put back yet: keeps the layout still.
          <div className="best-card best-card-idle flex w-full items-center justify-between gap-3 px-3 py-2 text-sm">
            <span className="min-w-0">
              <span className="muted block text-xs font-medium tracking-wide uppercase">Best so far</span>
              <span className="muted block truncate">
                {record.bestFound
                  ? `${record.bestFound.words.join(' + ')} · ${record.bestFound.score} pts (on the board)`
                  : 'Your best placement will be kept here'}
              </span>
            </span>
          </div>
        ))}

      {/* Once the day's play is made, Game shows results and top-play buttons here instead. */}
      {!finished && (
        <div className="grid grid-cols-3 gap-2">
          <button type="button" onClick={shuffle} className="btn-secondary">
            <span aria-hidden className="mr-1.5">⇄</span>Shuffle
          </button>
          <button type="button" onClick={recallAll} disabled={placed.length === 0} className="btn-secondary">
            <span aria-hidden className="mr-1.5">↶</span>Recall
          </button>
          <button type="button" onClick={submit} disabled={!evaluation?.ok} className="btn-primary">
            {confirming ? 'Confirm' : 'Play'}
            {evaluation?.ok && (
              <span className="play-score ml-1.5 tabular-nums" aria-label={`for ${evaluation.play.score} points`}>
                {evaluation.play.score}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
